import { useLocation, useNavigate, useSearchParams } from "react-router";
import { ITEM_LIST_BESIDE_DETAIL, useMediaQuery } from "./useMediaQuery";

// The URL state of a page with tabs and an items list (`?tab=&item=&view=&proof=&focus=`), the same on the quote
// page, the CIS order page and a Tharstern-era order's. Every change to it REPLACES the history entry: switching
// tabs, choosing an item or an item's sub-tab, and the jumps to a panel move within one page, so the browser's Back
// leaves the page in one step, to where the customer came from (the Orders list), not back through what they clicked.
// The URL still updates, so a link and a reload land where the customer was.
//
// The one exception is a phone's item view. Below the list-beside-detail breakpoint the list and an item's detail are
// two screens, the second with its own "All items" button, so opening an item from the list pushes one entry and
// Back (or "All items") returns to the list. Moving on within that item (a sub-tab) replaces it again.

const OPENED_FROM_LIST = "openedFromList";

export type ParamsPatch = Record<string, string | null>;

export function useItemPageParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isListBesideDetail = useMediaQuery(ITEM_LIST_BESIDE_DETAIL);
  const cameFromList =
    (location.state as Record<string, unknown> | null)?.[OPENED_FROM_LIST] ===
    true;

  const write = (
    patch: ParamsPatch,
    options: { replace: boolean; state?: unknown },
  ) =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null) next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, options);

  /** A change within the page: replaces the entry, keeping a phone item view's mark that it was opened from the list. */
  const setParams = (patch: ParamsPatch) =>
    write(patch, { replace: true, state: location.state });

  /** The list's choice. `key` null is "All items" (a phone's way back to the list). */
  const selectItem = (key: string | null, extra: ParamsPatch = {}) => {
    if (key === null) {
      if (cameFromList) navigate(-1);
      else setParams({ item: null, ...extra });
      return;
    }
    const isOpeningPhoneView =
      !isListBesideDetail && searchParams.get("item") === null;
    if (isOpeningPhoneView)
      write(
        { item: key, ...extra },
        { replace: false, state: { [OPENED_FROM_LIST]: true } },
      );
    else setParams({ item: key, ...extra });
  };

  return { searchParams, setParams, selectItem };
}
